import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../lib/auth";
import { toast } from "sonner";
import { useSEO } from "../lib/useSEO";

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
export const startGoogleAuth = () => {
  const redirectUrl = window.location.origin + "/auth/callback";
  window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
};

export function Login() {
  useSEO({ title: "Login | Joy Pearl", description: "Sign in to your Joy Pearl account to track orders and continue shopping artificial jewellery and kids' gifts.", noIndex: true });
  const { login } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({ email: "", password: "" });
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const u = await login(form.email, form.password);
      toast.success("Welcome back");
      const dest = u?.role === "admin" ? "/admin" : u?.role === "seller" ? "/seller" : "/";
      navigate(dest);
    }
    catch (e) { toast.error(e?.response?.data?.detail || "Login failed"); }
    finally { setLoading(false); }
  };

  return (
    <div className="jp-container py-12 max-w-md" data-testid="login-page">
      <h1 className="font-serif text-4xl mb-2">Welcome back</h1>
      <p className="text-brand-muted text-sm mb-8">Sign in to continue to Joy Pearl.</p>
      <form onSubmit={submit} className="space-y-5">
        <input required type="email" placeholder="Email" className="jp-input" value={form.email} onChange={(e) => setForm({...form, email: e.target.value})} data-testid="login-email"/>
        <input required type="password" placeholder="Password" className="jp-input" value={form.password} onChange={(e) => setForm({...form, password: e.target.value})} data-testid="login-password"/>
        <button type="submit" disabled={loading} className="jp-btn-primary w-full" data-testid="login-submit">{loading ? "Signing in…" : "Sign In"}</button>
      </form>
      <div className="flex items-center gap-4 my-6 text-xs uppercase tracking-[0.2em] text-brand-muted">
        <span className="flex-1 border-t border-brand-line"/>or<span className="flex-1 border-t border-brand-line"/>
      </div>
      <button onClick={startGoogleAuth} className="jp-btn-outline w-full" data-testid="google-login-btn">
        Continue with Google
      </button>
      <p className="text-sm text-brand-muted mt-8 text-center">
        New to Joy Pearl? <Link to="/signup" className="text-brand-primary hover:underline">Create an account</Link>
      </p>
    </div>
  );
}

export function Signup() {
  useSEO({ title: "Create Account | Joy Pearl", description: "Create your Joy Pearl account to shop artificial jewellery and kids' gifts with COD across India.", noIndex: true });
  const { register } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({ name: "", email: "", password: "", phone: "", role: "customer", business_name: "" });
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const u = await register(form);
      toast.success(form.role === "seller" ? "Account created — your seller application is pending review." : "Welcome to Joy Pearl");
      const dest = u?.role === "admin" ? "/admin" : u?.role === "seller" ? "/seller" : "/";
      navigate(dest);
    } catch (e) { toast.error(e?.response?.data?.detail || "Signup failed"); }
    finally { setLoading(false); }
  };

  return (
    <div className="jp-container py-12 max-w-md" data-testid="signup-page">
      <h1 className="font-serif text-4xl mb-2">Create your account</h1>
      <p className="text-brand-muted text-sm mb-8">Join thousands shopping at Joy Pearl.</p>
      <form onSubmit={submit} className="space-y-5">
        <div className="flex gap-2 text-sm">
          <label className={`flex-1 cursor-pointer border p-3 text-center ${form.role === "customer" ? "border-brand-primary text-brand-primary" : "border-brand-line"}`}>
            <input type="radio" name="role" checked={form.role === "customer"} onChange={() => setForm({...form, role: "customer"})} className="sr-only"/>
            Shop as Customer
          </label>
          <label className={`flex-1 cursor-pointer border p-3 text-center ${form.role === "seller" ? "border-brand-primary text-brand-primary" : "border-brand-line"}`}>
            <input type="radio" name="role" checked={form.role === "seller"} onChange={() => setForm({...form, role: "seller"})} className="sr-only"/>
            Apply as Seller
          </label>
        </div>
        <input required placeholder="Full name" className="jp-input" value={form.name} onChange={(e) => setForm({...form, name: e.target.value})} data-testid="signup-name"/>
        <input required type="email" placeholder="Email" className="jp-input" value={form.email} onChange={(e) => setForm({...form, email: e.target.value})} data-testid="signup-email"/>
        <input required type="password" placeholder="Password (min 6 chars)" minLength={6} className="jp-input" value={form.password} onChange={(e) => setForm({...form, password: e.target.value})} data-testid="signup-password"/>
        <input placeholder="Phone (10-digit)" className="jp-input" value={form.phone} onChange={(e) => setForm({...form, phone: e.target.value})}/>
        {form.role === "seller" && (
          <input required placeholder="Business / Brand name" className="jp-input" value={form.business_name} onChange={(e) => setForm({...form, business_name: e.target.value})} data-testid="signup-business"/>
        )}
        <label className="flex items-start gap-2 text-xs text-brand-muted" data-testid="signup-consent">
          <input type="checkbox" required className="mt-0.5" defaultChecked/>
          <span>I agree to Joy Pearl's <Link to="/legal/terms" className="text-brand-primary hover:underline">Terms</Link> and <Link to="/legal/privacy" className="text-brand-primary hover:underline">Privacy Policy</Link>, and acknowledge my rights under the DPDP Act 2023.</span>
        </label>
        <button type="submit" disabled={loading} className="jp-btn-primary w-full" data-testid="signup-submit">{loading ? "Creating…" : "Create Account"}</button>
      </form>
      <p className="text-sm text-brand-muted mt-8 text-center">
        Already have an account? <Link to="/login" className="text-brand-primary hover:underline">Sign in</Link>
      </p>
    </div>
  );
}
